import { DataClientError } from "../errors";
import { FlowModel } from "@blazeo.com/calendar-client";

/**
 * Basic/Blazeo adapter for Flow operations.
 * Uses FlowModel from @blazeo.com/calendar-client for SmartHub/Basic plan users.
 */
export class SmartHubFlowsAdapter {
  constructor() {
    this.FlowModel = FlowModel;
  }

  /**
   * Get a single flow by ID
   * @param {Object} params - { flowId }
   * @param {Object} context - Additional context
   * @returns {Promise<Object>}
   */
  async getFlow(params, context = {}) {
    try {
      const { flowId } = params;
      if (!flowId) {
        throw new Error("flowId is required for getFlow");
      }

      const response = await this.FlowModel.get(String(flowId));
      return this._mapSingleToCRMFormat(response);
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flow Error",
        error
      );
    }
  }

  /**
   * Get list of flows
   * Note: FlowModel.list() does not support pagination natively, so we handle it client-side
   * @param {Object} params - { skip, take, where }
   * @param {Object} context - { companyKey }
   * @returns {Promise<{items: Array, totalCount: number}>}
   */
  async getFlows(params, context = {}) {
    try {
      const companyKey = context.companyKey;
      if (!companyKey) {
        throw new Error("companyKey is required in context for getFlows");
      }

      const includeDeleted = params?.where?.isDeleted?.eq ?? false;

      // Fetch all flows (Blazeo doesn't support server-side pagination for flows)
      const response = await this.FlowModel.list(companyKey, includeDeleted);
      const allFlows = Array.isArray(response)
        ? response
        : response?.items || response?.data || [];

      // Apply client-side pagination
      const skip = params?.skip || 0;
      const take = params?.take || 25;

      // Apply search filter if provided
      let filteredFlows = allFlows;
      if (params?.where) {
        filteredFlows = this._applyFilters(allFlows, params.where);
      }

      if (params?.order?.length) {
        filteredFlows = this._applySort(filteredFlows, params.order);
      }

      const paginatedItems = filteredFlows.slice(skip, skip + take);
      const totalCount = filteredFlows.length;

      return {
        items: this._mapToCRMFormat(paginatedItems),
        totalCount,
      };
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flows Error",
        error
      );
    }
  }

  /**
   * Get flows linked to a calendar
   * @param {Object} params - { calendarId, where }
   * @param {Object} context
   * @returns {Promise<{items: Array, totalCount: number}>}
   */
  async getFlowsByCalenderId(params, context = {}) {
    try {
      const calendarId = params?.calendarId || params?.calenderId;
      if (!calendarId) {
        throw new Error("calendarId is required for getFlowsByCalenderId");
      }

      const response = await this.FlowModel.listByCalendar(String(calendarId));
      return response;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flows By Calendar Id Error",
        error
      );
    }
  }

  /**
   * Save (create or update) a flow
   * @param {Object} params - Flow data
   * @param {Object} context - { companyKey }
   * @returns {Promise<Object>}
   */
  async saveFlow(params, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for saveFlow");

      const isUpdate = !!(params.flowId || params.id);
      const blazeoPayload = this._mapToBlazeoFormat(params, companyKey);

      let response;
      if (isUpdate) {
        // FlowModel.updateFlow takes single payload with flowId inside
        response = await this.FlowModel.updateFlow(blazeoPayload);
      } else {
        response = await this.FlowModel.createFlow(blazeoPayload);
      }

      return this._mapSingleToCRMFormat(response);
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Save Flow Error",
        error
      );
    }
  }

  /**
   * Delete a flow
   * @param {string} flowId
   * @param {Object} context
   * @returns {Promise<boolean>}
   */
  async deleteFlow(flowId, context = {}) {
    try {
      await this.FlowModel.delete(String(flowId));
      return true;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Delete Flow Error",
        error
      );
    }
  }

  /**
   * Duplicate/Clone a flow
   * @param {string} flowId
   * @param {Object} context - { newName }
   * @returns {Promise<Object>}
   */
  async duplicateFlow(flowId, context = {}) {
    try {
      const { newName } = context;
      const response = await this.FlowModel.duplicate(String(flowId), newName);
      return this._mapSingleToCRMFormat(response);
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Duplicate Flow Error",
        error
      );
    }
  }

  /**
   * Get flow appearance settings
   * @param {string} flowId
   * @param {Object} context
   * @returns {Promise<Object>}
   */
  async getFlowAppearance(flowId, context = {}) {
    try {
      const response = await this.FlowModel.getAppearance(String(flowId));
      return response;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flow Appearance Error",
        error
      );
    }
  }

  /**
   * Save flow appearance settings
   * @param {string} flowId
   * @param {Object} payload - Appearance data (appearanceJson, etc.)
   * @param {Object} context
   * @returns {Promise<Object>}
   */
  async saveFlowAppearance(flowId, payload, context = {}) {
    try {
      // FlowModel.saveAppearance takes single payload object with flowId inside
      const body = { ...payload, flowId: String(flowId) };
      const response = await this.FlowModel.saveAppearance(body);
      return response;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Save Flow Appearance Error",
        error
      );
    }
  }

  /**
   * Get flow embed settings
   * @param {string} flowId
   * @param {Object} context
   * @returns {Promise<Object>}
   */
  async getFlowEmbed(flowId, context = {}) {
    try {
      const response = await this.FlowModel.getEmbed(String(flowId));
      return response;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flow Embed Error",
        error
      );
    }
  }

  /**
   * Save flow embed settings
   * @param {string} flowId
   * @param {Object} payload - Embed data (embedJson, etc.)
   * @param {Object} context
   * @returns {Promise<Object>}
   */
  async saveFlowEmbed(flowId, payload, context = {}) {
    try {
      // FlowModel.saveEmbed takes single payload object with flowId inside
      const body = { ...payload, flowId: String(flowId) };
      const response = await this.FlowModel.saveEmbed(body);
      return response;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Save Flow Embed Error",
        error
      );
    }
  }

  /**
   * Get public flow data (for unauthenticated access)
   * @param {string} flowId
   * @param {Object} context
   * @returns {Promise<Object>} - { flow, appearance, embed }
   */
  async getPublicFlow(flowId, context = {}) {
    try {
      const response = await this.FlowModel.getPublic(String(flowId));
      // Response contains { flow, appearance, embed }
      return {
        status: response?.flow ? "success" : "failure",
        flow: response?.flow ? this._mapSingleToCRMFormat(response.flow) : null,
        appearance: response?.appearance ?? null,
        embed: response?.embed ?? null,
      };
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Public Flow Error",
        error
      );
    }
  }

  /**
   * Get flow preview data
   * @param {string} flowId
   * @param {Object} context
   * @returns {Promise<Object>} - { flow, appearance, embed }
   */
  async getFlowPreview(flowId, context = {}) {
    try {
      const response = await this.FlowModel.getPreview(String(flowId));
      // Response contains { flow, appearance, embed }
      return {
        flow: response?.flow ? this._mapSingleToCRMFormat(response.flow) : null,
        appearance: response?.appearance ?? null,
        embed: response?.embed ?? null,
      };
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Get Flow Preview Error",
        error
      );
    }
  }

  // ---------------------------------------------------------
  // Helper Methods (Data Mapping & Filtering)
  // ---------------------------------------------------------

  /**
   * Apply filters to flows array
   */
  _applyFilters(flows, where) {
    if (!where || typeof where !== "object") return flows;

    return flows.filter((flow) => {
      // Handle name search
      if (where.name?.contains) {
        const searchText = where.name.contains.toLowerCase();
        const flowName = (flow.name || "").toLowerCase();
        if (!flowName.includes(searchText)) return false;
      }

      // Handle isActive filter
      if (where.isActive?.eq !== undefined) {
        if (flow.isActive !== where.isActive.eq) return false;
      }

      // Handle status text filter (matches "active" / "inactive" display text)
      if (where.status?.contains) {
        const searchText = where.status.contains.toLowerCase();
        const statusText = flow.isActive ? "active" : "inactive";
        if (!statusText.includes(searchText)) return false;
      }

      // Handle isDeleted filter
      if (where.isDeleted?.eq !== undefined) {
        if (flow.isDeleted !== where.isDeleted.eq) return false;
      }

      // Handle id/flowId filter
      if (where.id?.eq !== undefined || where.flowId?.eq !== undefined) {
        const targetId = where.id?.eq || where.flowId?.eq;
        if (flow.id !== targetId && flow.flowId !== targetId) return false;
      }

      return true;
    });
  }

  /**
   * Apply sorting to flows array before pagination
   */
  _applySort(flows, order = []) {
    const [{ field, dir } = {}] = order;
    if (!field || !dir) return flows;

    const sorted = [...flows];
    sorted.sort((a, b) => {
      const left =
        field === "createdOn"
          ? a.createdOn || ""
          : (a.name || "").toLowerCase();
      const right =
        field === "createdOn"
          ? b.createdOn || ""
          : (b.name || "").toLowerCase();

      if (left < right) return dir === 1 ? -1 : 1;
      if (left > right) return dir === 1 ? 1 : -1;
      return 0;
    });

    return sorted;
  }

  /**
   * Convert CRM format to Blazeo API format
   */
  _mapToBlazeoFormat(crmPayload, companyKey) {
    const payload = {
      companyKey: companyKey,
      name: crmPayload.name || crmPayload.title || "",
      description: crmPayload.description || "",
      flowJson:
        crmPayload.flowData || crmPayload.flowJson || crmPayload.nodes || "{}",
      isActive: crmPayload.isActive !== undefined ? crmPayload.isActive : true,
      isDeleted: crmPayload.isDeleted || false,
      createdOn: crmPayload.createdOn,
      modifiedOn: crmPayload.modifiedOn,
    };

    // Only include flowId if it exists (for updates) - don't send null for creates
    const flowId = crmPayload.flowId || crmPayload.id;
    if (flowId) {
      payload.flowId = flowId;
    }

    return payload;
  }

  /**
   * Convert Blazeo format to CRM standard format
   */
  _mapToCRMFormat(blazeoFlows) {
    if (!Array.isArray(blazeoFlows)) return [];
    return blazeoFlows
      .map((f) => this._mapSingleToCRMFormat(f))
      .filter(Boolean);
  }

  _mapSingleToCRMFormat(flow) {
    if (!flow) return null;

    // Handle flowJson being a string or object
    let flowData = flow.flowJson;
    if (typeof flowData === "string") {
      try {
        flowData = JSON.parse(flowData);
      } catch {
        flowData = {};
      }
    }

    return {
      // Dual casing for compatibility with both Pro and Basic UIs
      Id: flow.id || flow.flowId,
      id: flow.id || flow.flowId,
      FlowId: flow.flowId || flow.id,
      flowId: flow.flowId || flow.id,
      CompanyKey: flow.companyKey,
      companyKey: flow.companyKey,
      Name: flow.name || flow.title,
      name: flow.name || flow.title,
      Title: flow.name || flow.title,
      title: flow.name || flow.title,
      Description: flow.description,
      description: flow.description,
      FlowData: flowData,
      flowData: flowData,
      FlowJson: flow.flowJson,
      flowJson: flow.flowJson,
      IsActive: flow.isActive ?? true,
      isActive: flow.isActive ?? true,
      IsDeleted: flow.isDeleted || false,
      isDeleted: flow.isDeleted || false,
      CreatedOn: flow.createdOn,
      createdOn: flow.createdOn,
      ModifiedOn: flow.modifiedOn,
      modifiedOn: flow.modifiedOn,
    };
  }
}
